import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import CheckCircle from '@mui/icons-material/CheckCircle'
import PlaceOutlined from '@mui/icons-material/PlaceOutlined'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../api/client'
import { keys } from '../api/hooks'
import type { InviteResult, MeetingInvite } from '../api/types'
import { fmtLeads } from '../lib/schedule'
import { useUi } from '../store/ui'
import { useTokens } from '../theme'

/** Reminder offsets the server books, shown before the user answers. Kept in step with REMINDER_OFFSETS_MINUTES on the server. */
const DEFAULT_LEADS = [120, 30]

const range = (i: MeetingInvite) => {
  const s = new Date(i.start), e = new Date(i.end)
  if (i.allDay) return `${s.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })} · all day`
  const day = s.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
  const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  return `${day} · ${time(s)} – ${time(e)}`
}

/** A meeting invitation found in a message. "Yes" puts it on the calendar and books the reminder emails. */
export function InviteCard({ id, invite, accepted }: { id: string; invite: MeetingInvite; accepted: boolean }) {
  const t = useTokens()
  const qc = useQueryClient()
  const notify = useUi((s) => s.notify)
  const [declined, setDeclined] = useState(false)
  const [result, setResult] = useState<InviteResult | null>(null)
  const past = new Date(invite.end).getTime() < Date.now()
  const yes = useMutation({
    mutationFn: () => api<InviteResult>(`/messages/${id}/invite/respond`, { method: 'POST', body: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } }),
    onSuccess: (r) => {
      setResult(r)
      void qc.invalidateQueries({ queryKey: keys.message(id) })
      void qc.invalidateQueries({ queryKey: ['calendar-events'] })
      const rem = r.reminders.length ? `Reminders by email ${fmtLeads(r.reminders)} before.` : 'Too close to the start for reminder emails.'
      notify(r.calendar === 'failed'
        ? { message: `Reminders are set, but the calendar could not be updated${r.calendarError ? `: ${r.calendarError}` : ''}`, severity: 'error', durationMs: 9000 }
        : { message: `${r.calendar === 'already' ? 'Already on your calendar.' : 'Added to your calendar.'} ${rem}`, severity: 'success', durationMs: 6000 })
    },
    onError: (e) => notify({ message: e instanceof Error ? e.message : 'Could not accept the invitation', severity: 'error' }),
  })
  if (declined) return null

  const going = accepted || Boolean(result)
  const s = new Date(invite.start)
  const leads = result?.reminders ?? DEFAULT_LEADS
  return (
    <Box role="group" aria-label="Meeting invitation" sx={{ mx: 3, my: 1, p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderRadius: '14px', bgcolor: t.canvas, border: 1, borderColor: 'divider' }}>
      <Box aria-hidden sx={{ width: 52, height: 56, flexShrink: 0, borderRadius: '12px', bgcolor: 'background.paper', border: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: t.accent, lineHeight: 1 }}>{s.toLocaleDateString([], { month: 'short' }).toUpperCase()}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }} className="tabular">{s.getDate()}</Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{invite.title || '(no title)'}</Typography>
        <Typography variant="body2" color="text.secondary" className="tabular">{range(invite)}</Typography>
        {invite.location && <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PlaceOutlined sx={{ fontSize: 15 }} />{invite.location}</Typography>}
        {going && (
          <Typography variant="body2" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, color: t.accent, fontWeight: 600 }}>
            <CheckCircle sx={{ fontSize: 16 }} />You're going{leads.length ? ` · reminders by email ${fmtLeads(leads)} before` : ''}
          </Typography>
        )}
      </Box>
      {!going && !past && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>Going?<br />Adds to calendar, emails you {fmtLeads(DEFAULT_LEADS)} before</Typography>
          <Button variant="contained" disableElevation onClick={() => yes.mutate()} disabled={yes.isPending} startIcon={yes.isPending ? <CircularProgress size={16} color="inherit" /> : <CalendarMonthOutlined />} sx={{ borderRadius: '10px' }}>Yes</Button>
          <Button color="inherit" onClick={() => setDeclined(true)} disabled={yes.isPending} sx={{ borderRadius: '10px' }}>No</Button>
        </Box>
      )}
      {!going && past && <Typography variant="body2" color="text.secondary">This meeting has ended.</Typography>}
    </Box>
  )
}
